/**
 * useReadAloud.js
 * ================
 * Announces gaze options aloud when config.readAloud is enabled.
 *
 * Each direction is spoken as two separate utterances with a pause between:
 *   "Look left for"  →  [pause]  →  "BATHROOM"
 *
 * Repeats config.readAloudRepeat extra times unless cancelled by unmount
 * or a dependency change (e.g. navigating to a new page).
 *
 * Usage:
 *   useReadAloud(segments, [dep1, dep2]);
 *
 * segments: array of [cue, label?] pairs, e.g.:
 *   [
 *     ['Look left for',  'Respond'],
 *     ['Look right for', 'Need'],
 *     ['Look down for',  'Ask'],
 *     ['Look up to go back'],
 *   ]
 * deps: additional values that should restart the announcement (e.g. node id, options array)
 */

import { useEffect, useRef } from 'react';
import { useGazeConfig } from './GazeConfigContext';

const PAUSE_MS     = 350;   // gap between direction cue and option label
const ROUND_GAP_MS = 600;   // gap between repeat rounds
const START_DELAY  = 120;   // wait after cancel() before speaking — avoids browser race condition
const RATE         = 0.82;

function makeUtterance(text, voice) {
  const u = new SpeechSynthesisUtterance(text);
  u.rate   = RATE;
  u.pitch  = 1.0;
  u.volume = 1.0;
  if (voice) u.voice = voice;
  return u;
}

// Chrome sometimes fires onend more than once for a single utterance.
// Wrap callbacks so they only execute on the first fire.
function once(fn) {
  let fired = false;
  return (...args) => { if (!fired) { fired = true; fn(...args); } };
}

export default function useReadAloud(segments, deps = []) {
  const { config } = useGazeConfig();

  // Keep a ref to the latest segments so the effect closure always reads
  // the current list even if it was computed outside the effect.
  const segmentsRef = useRef(segments);
  segmentsRef.current = segments;

  useEffect(() => {
    if (!config.readAloud) return;

    const totalRuns = 1 + config.readAloudRepeat;
    let cancelled   = false;
    const timers    = [];

    function speakSegment(segIndex, runIndex, voice) {
      if (cancelled) return;
      const segs = segmentsRef.current;
      if (!segs.length) return;

      if (segIndex >= segs.length) {
        if (runIndex + 1 >= totalRuns) return;
        const t = setTimeout(() => speakSegment(0, runIndex + 1, voice), ROUND_GAP_MS);
        timers.push(t);
        return;
      }

      const [cue, label] = segs[segIndex];

      if (label) {
        const cueUtt = makeUtterance(cue, voice);
        cueUtt.onend = once(() => {
          if (cancelled) return;
          const t = setTimeout(() => {
            if (cancelled) return;
            const labelUtt = makeUtterance(label, voice);
            labelUtt.onend = once(() => speakSegment(segIndex + 1, runIndex, voice));
            window.speechSynthesis?.speak(labelUtt);
          }, PAUSE_MS);
          timers.push(t);
        });
        window.speechSynthesis?.speak(cueUtt);
      } else {
        const u = makeUtterance(cue, voice);
        u.onend = once(() => speakSegment(segIndex + 1, runIndex, voice));
        window.speechSynthesis?.speak(u);
      }
    }

    function startWhenVoicesReady() {
      if (cancelled) return;
      // Chrome bug: cancel() leaves synthesis in a paused state.
      window.speechSynthesis?.resume();
      const voice = config.voiceURI
        ? (window.speechSynthesis.getVoices().find(v => v.voiceURI === config.voiceURI) ?? null)
        : null;
      speakSegment(0, 0, voice);
    }

    // Cancel any leftover speech from the previous page, then wait a brief
    // moment before starting — some browsers need this gap after cancel().
    window.speechSynthesis?.cancel();
    const startTimer = setTimeout(() => {
      if (!window.speechSynthesis) return;
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) {
        startWhenVoicesReady();
      } else {
        // Voices load asynchronously — wait before speaking.
        window.speechSynthesis.onvoiceschanged = () => {
          window.speechSynthesis.onvoiceschanged = null;
          startWhenVoicesReady();
        };
      }
    }, START_DELAY);
    timers.push(startTimer);

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      window.speechSynthesis?.cancel();
      if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.readAloud, config.readAloudRepeat, config.voiceURI, ...deps]);
}
